/// <amd-module name="Build/Admin/Pool/Components/PoolDiagnosticsContainer" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { css } from "VSSUI/Util";
import { ZeroData } from "VSSUI/ZeroData";

import { ElasticPoolLog, ElasticNode, ElasticNodeState } from "DistributedTask/Client/Contracts/TaskAgent";

import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { ConfirmButtonType } from "Build/Admin/Common/Actions/ConfirmationDialogActions";
import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { Assets, FwLinks, ServiceEndpointConstants } from "Build/Admin/Common/Constants";
import { getAzurePortalVmssUrl } from "Build/Admin/Common/ElasticPoolUtils";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { ElasticNodeList } from "Build/Admin/Pool/Components/ElasticNodeList";
import { ElasticPoolLogList } from "Build/Admin/Pool/Components/ElasticPoolLogList";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IPoolDiagnosticsContainerProps {
    actionCreator: PoolActionCreator;
    storesHub: StoresHub;
}

export interface IPoolDiagnosticsContainerState {
    serviceEndpoint: ServiceEndpoint | undefined;
    virtualMachineScaleSet: VirtualMachineScaleSet | undefined;
    logs: ElasticPoolLog[];
    elasticNodes: ElasticNode[];
    renderZeroData: boolean;
}

export class PoolDiagnosticsContainer extends VssComponent<IPoolDiagnosticsContainerProps, IPoolDiagnosticsContainerState> {
    constructor(props: IPoolDiagnosticsContainerProps) {
        super(props);
        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.storesHub.elasticNodeStore.addChangedListener(this._onChanged);
        this.props.storesHub.elasticPoolLogStore.addChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.elasticNodeStore.removeChangedListener(this._onChanged);
        this.props.storesHub.elasticPoolLogStore.removeChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
    }

    public render(): JSX.Element {
        return (
            <div className="pipelines-pool-diagnostics page-content page-content-top">
                <ConditionalChildren renderChildren={this.state.elasticNodes.length > 0}>
                    <Card
                        titleProps={{ text: Resources.SavedAgents, size: TitleSize.Medium}}
                        headerDescriptionProps={{ text: this._getSavedAgentsCardDescription() }}
                        className={css("flex-grow bolt-table-card", this.state.renderZeroData ? undefined : "margin-bottom-16")}>
                        <ElasticNodeList elasticNodes={this.state.elasticNodes} onDeleteNode={this._onDeleteNode} />
                    </Card>
                </ConditionalChildren>
                <ConditionalChildren renderChildren={this.state.renderZeroData}>
                    <ZeroData
                        primaryText={Resources.ZeroLogsPrimaryText}
                        imagePath={getAssetLocation(this.context.pageContext, Assets.ZeroDataPng)}
                        imageAltText={Resources.ZeroLogsPrimaryText} />
                </ConditionalChildren>
                <ConditionalChildren renderChildren={!this.state.renderZeroData}>
                    <Card
                        titleProps={{ text: Resources.ElasticLogsCardTitle, size: TitleSize.Medium}}
                        headerDescriptionProps={{ text: this._getLogsCardDescription() }}
                        className="flex-grow bolt-table-card">
                        <ElasticPoolLogList logs={this.state.logs} />
                    </Card>
                </ConditionalChildren>
            </div>
        );
    }

    private _getSavedAgentsCardDescription(): JSX.Element {
        return (
            <span>
                {Resources.SavedAgentsDescription}
                <Link className="margin-left-4" href={FwLinks.ElasticPoolTroubleshooting} target="_blank">{Resources.SavedAgentsLearnMore}</Link>
            </span>
        );
    }

    private _getLogsCardDescription(): JSX.Element {
        return (
            <>
                <div>
                    {this._getVirtualMachineScaleSetLogsLink(this.state.serviceEndpoint, this.state.virtualMachineScaleSet)}
                </div>
                <div>
                    <Link href={FwLinks.ElasticPoolTroubleshooting} target="_blank">{Resources.ElasticLogsLearnMore}</Link>
                </div>
            </>
        );
    }

    private _getVirtualMachineScaleSetLogsLink(serviceEndpoint: ServiceEndpoint | undefined, virtualMachineScaleSet: VirtualMachineScaleSet | undefined): JSX.Element {
        if (serviceEndpoint?.authorization?.parameters &&
            serviceEndpoint?.authorization?.parameters[ServiceEndpointConstants.AzureTenantId] &&
            virtualMachineScaleSet?.id) {
            return (
                <Link
                    href={getAzurePortalVmssUrl(serviceEndpoint.authorization.parameters[ServiceEndpointConstants.AzureTenantId], virtualMachineScaleSet.id, "eventlogs")}
                    target="_blank">
                    {Resources.VmssLogsLink}
                </Link>
            );
        }
        else {
            return <span>{Resources.VmssLogsLink}</span>;
        }
    }

    private _onDeleteNode = (elasticNode: ElasticNode): void => {
        this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
            elasticNode,
            Resources.ConfirmDeleteTitle,
            format(Resources.ConfirmDeleteNodeMessage, elasticNode.name),
            ConfirmButtonType.Delete,
            this._confirmDeleteNode,
            this._cancelConfirmationDialog);
    }

    private _confirmDeleteNode = (elasticNode: ElasticNode): void => {
        this.props.actionCreator.deleteElasticNode(elasticNode);
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _cancelConfirmationDialog = (): void => {
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IPoolDiagnosticsContainerState {
        return {
            serviceEndpoint: this.props.storesHub.selectionStore.state.selectedServiceEndpoint,
            virtualMachineScaleSet: this.props.storesHub.selectionStore.state.selectedVirtualMachineScaleSet,
            logs: this.props.storesHub.elasticPoolLogStore.state.elasticPoolLogs,
            elasticNodes: this.props.storesHub.elasticNodeStore.state.elasticNodes.filter(n => n.state === ElasticNodeState.Saved),
            renderZeroData: this.props.storesHub.elasticPoolLogStore.state.elasticPoolLogs.length === 0
        };
    }
}
